import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Edit, Trash2 } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";

interface ObjectsListProps {
    selectedType: string;
    data: any[];
    onSelect: (object: any) => void;
    onDelete: (id: string) => void;
    selectedObjectId?: string;
}

export function ObjectsList({
    selectedType,
    data,
    onSelect,
    onDelete,
    selectedObjectId,
}: ObjectsListProps) {
    const [deleteId, setDeleteId] = React.useState<string | null>(null);

    // Get field names from first object, or use defaults if no data
    const objectTypesMap = new Map([
        ["certifiedStudents", "studentsName"],
        ["courses", "courseName"],
        ["modules", "moduleName"],
        ["users", "emailId"],
        ["courseAttandance", "batchName"],
        ["courseBatches", "batchName"],
        ["courseTodayClasses", "batchName"],
        ["courseSubjects", "courseId"],
        ["students", "emailId"],
        ["employees", "employeesName"],
        ["allSubjects", "className"],
        ["classScheduleBatches", "className"], // Assuming you want to map this to "className"
        ["roles", "emailId"]
    ]);

    // If no data, show message
    if (data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-40 bg-muted/20 rounded-md border border-dashed">
                <p className="text-muted-foreground">No objects found</p>
                <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto h-full overflow-auto" >
            <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                        <TableHead className="w-[30%]">Id</TableHead>
                        <TableHead className="w-[50%]">Field Name</TableHead>
                        <TableHead className="w-[20%] text-right sticky right-0 bg-background">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.map((item) => {
                        const fieldName =
                           item[objectTypesMap.get(selectedType)]

                        return (
                            <TableRow
                                key={item.id}
                                className={`${selectedObjectId === item.id ? "bg-muted/50" : ""
                                    } animate-fade-in`}
                            >
                                <TableCell className="font-mono text-sm">
                                    <div className="flex items-center">
                                        <span className="text-blue-500">👤</span>
                                        <span className="ml-2 truncate max-w-[200px]">
                                            {item.id.substring(0,10)}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center">
                                        <span className="text-blue-500">📄</span>
                                        <span className="ml-2 truncate max-w-[300px]">
                                            {fieldName}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="text-right sticky right-0 bg-background">
                                    <div className="flex justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onSelect(item)}
                                            className="hover:bg-muted transition-colors duration-200"
                                        >
                                            <Edit className="h-4 w-4 mr-0" />
                                          
                                        </Button>
                                        <AlertDialog open={deleteId === item.id} onOpenChange={() => setDeleteId(null)}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                                                onClick={() => setDeleteId(item.id)}
                                            >
                                                <Trash2 className="h-4 w-4 mr-0" />
                                             
                                            </Button>
                                            <AlertDialogContent className="animate-scale-in">
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        This action cannot be undone. This will permanently delete the object.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        className="bg-destructive hover:bg-destructive/90"
                                                        onClick={() => {
                                                            onDelete(item.id);
                                                            setDeleteId(null);
                                                        }}
                                                    >
                                                        Delete
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}